import styled from 'styled-components';
import { InformationCircleOutlineIc } from '@dsch/dd-icons';

import { media } from 'helpers/breakpoints';
import { Link } from '../../../components/Toolkit/Button/Link';
import { Tooltip } from '../../../components/Toolkit/Tooltip/Tooltip';
import { theme } from 'helpers/Theme';

type Variants = 'DEFAULT' | 'SMALL';

export interface KeyInfoListProps {
  singleColumnOnDesktop?: boolean;
  infoList: InfoListItem[];
  cdnUrl?: string;
  variant?: Variants;
  className?: string;
  keyDark?: boolean;
}

export interface InfoListItem {
  value?: string;
  displayName: string;
  status?: 'ACTIVE' | 'CLOSED';
  infoTip?: {
    icon?: string;
    buttonText?: string;
    description?: string;
    title?: string;
    buttonLink?: string;
  };
  link?: {
    action?: string;
    external?: boolean;
    title?: string;
    url?: string;
  };
  badge?: {
    showBadge?: boolean;
    id?: number;
    displayName?: string;
  };
}

const Grid = styled.div<{ singleColumnOnDesktop: boolean; variant?: Variants }>`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: ${({ theme, variant }) =>
    variant === 'DEFAULT' ? theme.spacing.M16 : theme.spacing.S4};

  ${media.medium} {
    grid-template-columns: ${({ singleColumnOnDesktop }) =>
      singleColumnOnDesktop ? '1fr' : '1fr 1fr'};
    grid-column-gap: ${({ theme }) => theme.spacing.L56};
  }
`;

const KeyInfoListItem = styled.div<{ variant: Variants }>`
  ${({ theme, variant }) =>
    variant === 'DEFAULT' ? theme.fontSize.M16 : theme.fontSize.B12};
  display: flex;
  justify-content: space-between;
  text-align: right;
`;

export const Text = styled.div<{
  variant: Variants;
  isValue?: boolean;
  status?: 'ACTIVE' | 'CLOSED';
}>`
  color: ${({ theme, isValue }) =>
    isValue ? theme.colors.GREY_DARKER : theme.colors.GREY_DARK};
  ${({ status }) =>
    status &&
    (status === 'ACTIVE'
      ? `color: ${theme.colors.GREEN_DARK};`
      : `color: ${theme.colors.RED_DARK};`)}
  font-weight: ${({ theme, isValue, variant }) =>
    variant === 'DEFAULT' && isValue
      ? theme.fontWeight.bold
      : theme.fontWeight.regular};
  img {
    margin-left: ${({ theme }) => theme.spacing.S4};
  }
`;

const SIoInformationCircleOutline = styled(InformationCircleOutlineIc)`
  color: ${({ theme }) => theme.colors.BLUE};
  margin-left: ${({ theme }) => theme.spacing.S4};
  cursor: pointer;
`;

function KeyInfoList({
  singleColumnOnDesktop = false,
  infoList,
  cdnUrl,
  variant = 'DEFAULT',
  className,
}: KeyInfoListProps) {
  const ListItems =
    infoList &&
    infoList.map((infoListItem: InfoListItem, index: number) => {
      return (
        <KeyInfoListItem key={index} variant={variant}>
          <Text status={infoListItem.status} variant={variant}>
            {infoListItem.displayName}
            {infoListItem.badge?.showBadge && (
              <img
                /** PENDING: Switch out for tag  */
                src={`${cdnUrl}/images/illustrations/new-badge.svg`}
                data-testid="new-badge"
                height="16"
                width="32"
                alt="Badge displaying the word 'New'"
              />
            )}
          </Text>
          {infoListItem.link ? (
            <Text isValue variant={variant}>
              <Link
                ofType="SECONDARY"
                fontSize="B14"
                href={infoListItem.link.url}
                target={infoListItem.link.external ? '_blank' : '_self'}
                rel={infoListItem.link.external ? 'noopener' : undefined}
              >
                {infoListItem.link.title}
              </Link>
            </Text>
          ) : (
            <Text isValue status={infoListItem.status} variant={variant}>
              {infoListItem.value && infoListItem.value.length > 0
                ? `${infoListItem.value}`
                : '---'}
              {infoListItem.infoTip && (
                <Tooltip
                  title={infoListItem.infoTip.title}
                  subtitle={infoListItem.infoTip.description}
                  hasCloseOption={true}
                  hasButton={false}
                  tooltipPosition={'below'}
                  arrowPosition={['right', 'right', 'right']}
                  hideArrow={true}
                  displayInline={true}
                >
                  <SIoInformationCircleOutline
                    width="18"
                    height="18"
                    data-testid="info-icon"
                  />
                </Tooltip>
              )}
            </Text>
          )}
        </KeyInfoListItem>
      );
    });

  return (
    <Grid
      className={className}
      singleColumnOnDesktop={singleColumnOnDesktop}
      variant={variant}
    >
      {ListItems}
    </Grid>
  );
}

export { KeyInfoList };
